import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';

/**
 * Whether a model portfolio is a household's own private copy rather than one
 * shared across the shelf.
 *
 * product-service keeps the binding itself (`boundObjectId`) off the org API,
 * so this reads the marks cloning leaves behind: it takes a copy off the shelf
 * by forcing `suggestable` and `assignable` off when, and only when, the copy
 * is bound to an entity. An admin duplicating a shelf model also gets a
 * `cloneOf`, but keeps the flags it inherited from the master.
 *
 * The cost of being wrong is a refused mutation rather than a shared model
 * being edited: reading a private copy as a shelf model sends its own id as
 * the base, which product-service rejects outright ("Cannot customize from an
 * entity-bound clone"), and reading a shelf copy as private sends its master,
 * which is either the household's existing copy or a mismatch product-service
 * refuses.
 */
export const isEntityBoundModel = (
  model: Pick<
    HouseholdModelPortfolioModel,
    'cloneOf' | 'suggestable' | 'assignable'
  >,
): boolean =>
  !!model.cloneOf && model.suggestable === false && model.assignable === false;

/**
 * The shelf model to set a household's private copy up from.
 *
 * `setupEntityBoundModel` reuses the copy a household already has only when the
 * base given matches the master it was cloned from, so an already-customized
 * household has to be named by its master rather than by the copy in front of
 * the advisor.
 */
export const baseModelPortfolioId = (
  model: HouseholdModelPortfolioModel,
): string =>
  isEntityBoundModel(model) ? (model.cloneOf ?? model.id) : model.id;
